// AUTH
import { AuthContext, AuthProvider } from './auth/AuthContext';
import accessControl from './auth/accessControl';

// MOCKS
import MockProvider, {
  MockContext,
} from './components/mock-provider/MockProvider';

// API
import { APIProvider, APIContext } from './api/APIProvider';

// ASSETS
import AppIcons, { getIcon } from './icons/AppIcons';

// COMPONENTS
import AppLauncherPad from './components/app-launcher/AppLauncherPad';
import ImpersonationWrapper, {
  ImpersonationContext,
} from './components/impersonation-wrapper/ImpersonationWrapper';
import Faq from './components/faq/Faq';
import LoadingScreen from './components/loading-screen/LoadingScreen';
import ErrorPageWrapper from './components/error-page-wrapper/ErrorPageWrapper';
import ErrorPage from './components/error-page-wrapper/ErrorPage';
import GenericError from './components/error-screen/generic-error/GenericError';
import MegaWrapper from './components/mega-wrapper/MegaWrapper';
import PageNotFound from './components/page-not-found/PageNotFound';
import EncumbranceNotification from './components/encumbrance-notification/EncumbranceNotification';
import MenuProvider, {
  MenuContext,
} from './components/menu-provider/MenuProvider';
import UserMenu from './components/user-menu/UserMenu';
import {
  UserInitials,
  UserInitialsContent,
  UserInitialsItem,
  UserInitialSection,
  UserInitialsTrigger,
  USER_INITIALS_TRIGGER_ID,
} from './components/userInitials';
import RenderAnimation from './components/render-animation/RenderAnimation';
import NotificationProvider, {
  NotificationContext,
} from './components/notifications/NotificationProvider';
import NotificationBadge from './components/notifications/notification/NotificationBadge';
import {
  AccessibilityContext,
  AccessibilityProvider,
} from './components/accessibility-provider/AccessibilityProvider';
import ChatBotWrapper, {
  ChatBotContext,
} from './components/chatbot-wrapper/ChatBotWrapper';
import FirestoreWritePermissionProvider, {
  FirestoreWritePermissionContext,
} from './components/firestore-write-permission-provider/FirestoreWritePermissionProvider';

// UTILS
import {
  capitaliseFirstWord,
  capitaliseAllWords,
  replaceUnderscoresWithSpaces,
  convertLinks,
  isStringEmpty,
} from './utils/string';
import { deepClone } from './utils/deep-clone';
import { formatAmount } from './utils/format-amount';
import { formatUrl } from './utils/format-url';
import { isNumber } from './utils/number';
import {
  isObject,
  isDomNodeType,
  isDefined,
  isBoolean,
  isFunction,
} from './utils/misc';
import { isUrl } from './utils/is-url';
import portalLink from './utils/portalLinks';
import getFacultyfromCode from './utils/faculty';
import { swapAnchorForExternalLink } from './utils/swapAnchorForExternalLink';
import { useOnOutsideClick } from './hooks/use-on-outside-click';
import { useSessionStorage } from './hooks/use-session-storage';
import { useAppLauncherItems } from './hooks/use-app-launcher-items';
import { useUserMenuAvatar } from './components/userInitials/hooks/use-user-menu-avatar';
import { useUserMenuProfile } from './components/userInitials/hooks/use-user-menu-profile';
import { sortCourseEnrolmentsByRelevance } from './utils/sort-course-enrolments-by-relevance';
import { getEnvironmentValue } from './utils/get-environment-value';

// DATA
import categories from './data/categories';
import courseEnrolmentStatusProps from './data/course-enrolment-status-props';

// CONSTANTS
import COURSE_STATUS from './constants/course-status';
import EXTERNAL_LINKS from './constants/external-links';
import { SESSION_STORAGE_KEYS } from './constants/session-storage-keys';

// FIRESTORE
import {
  fsDocRef,
  fsGetDoc,
  fsSetDoc,
  fsUpdateDoc,
  fsDeleteDoc,
  fsWriteBatch,
  fsDocListener,
  fsCollectionListener,
  fsDeleteField,
} from './firebase/firestore';

// FEATURE TOGGLE
import FeatureProvider, {
  FeatureContext,
} from './feature-toggle/FeatureProvider';

// MAINTENANCE PROVIDER
import MaintenanceProvider, {
  MaintenanceContext,
} from './components/maintenance-provider/MaintenanceProvider';

export * from './api/api';
export { useApi } from './hooks/use-api';

export {
  // AUTH
  AuthContext,
  AuthProvider,
  accessControl,

  // MOCKS
  MockProvider,
  MockContext,

  // API
  APIProvider,
  APIContext,

  // ASSETS
  getIcon,
  AppIcons,

  // COMPONENTS
  AppLauncherPad,
  ImpersonationWrapper,
  ImpersonationContext,
  Faq,
  LoadingScreen,
  ErrorPageWrapper,
  ErrorPage,
  GenericError,
  MegaWrapper,
  PageNotFound,
  EncumbranceNotification,
  MenuProvider,
  MenuContext,
  UserMenu,
  UserInitials,
  UserInitialsContent,
  UserInitialsItem,
  UserInitialSection,
  UserInitialsTrigger,
  USER_INITIALS_TRIGGER_ID,
  RenderAnimation,
  NotificationBadge,
  NotificationProvider,
  NotificationContext,
  ChatBotWrapper,
  ChatBotContext,
  FirestoreWritePermissionProvider,
  FirestoreWritePermissionContext,

  // UTILS
  capitaliseFirstWord,
  capitaliseAllWords,
  replaceUnderscoresWithSpaces,
  convertLinks,
  isStringEmpty,
  deepClone,
  formatAmount,
  formatUrl,
  isNumber,
  isObject,
  isDomNodeType,
  isDefined,
  isBoolean,
  isFunction,
  isUrl,
  portalLink,
  getFacultyfromCode,
  swapAnchorForExternalLink,
  useOnOutsideClick,
  useSessionStorage,
  useAppLauncherItems,
  useUserMenuAvatar,
  useUserMenuProfile,
  sortCourseEnrolmentsByRelevance,
  getEnvironmentValue,
  AccessibilityContext,
  AccessibilityProvider,

  // DATA
  categories,
  courseEnrolmentStatusProps,

  // CONSTANTS
  COURSE_STATUS,
  EXTERNAL_LINKS,
  SESSION_STORAGE_KEYS,

  // FIRESTORE
  fsDocRef,
  fsGetDoc,
  fsSetDoc,
  fsUpdateDoc,
  fsDeleteDoc,
  fsWriteBatch,
  fsDocListener,
  fsCollectionListener,
  fsDeleteField,

  // FEATURE TOGGLE
  FeatureContext,
  FeatureProvider,

  // MAINTENANCE
  MaintenanceProvider,
  MaintenanceContext,
};
